import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Save, Plus, Trash2, ArrowLeft, X, FileText, Lock, ShieldCheck, AlertCircle, UserCheck, BarChart3, Award, Eye, FileCheck, Check, Shield, BookOpen, Users, TrendingUp, Link as LinkIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as HeroIconsOutline from '@heroicons/react/24/outline';


const sanitizeAndFormatHtml = (content) => {
    if (!content || typeof content !== 'string') return '';
    let html = content;
    html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
        const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\\?["']?\s*)([^"'\\>]+?)(?:\s*\\?["']?\s*)(?:>|\s|$)/i);
        if (!attrMatch) return tag;
        const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
        const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
        const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
        return `<a href="${rawUrl}"${targetAttr}>`;
    });
    if (!/<(?:br|p|div)\b/i.test(html)) {
        html = html.replace(/\r?\n/g, '<br />');
    }
    return html;
};

const DEFAULT_TRANSPARENCY_NOTICE = '<p>Investors and stakeholders can access Aham Housing Finance’s financial and regulatory disclosures through our <a href="https://ahamhfc.com/investor-information"><strong><u>investor information</u></strong></a> section.</p>';

const LucideIconMap = {
    FileText, Lock, ShieldCheck, AlertCircle, UserCheck, BarChart3, Award, Eye, FileCheck, Shield, BookOpen, Users, TrendingUp
};
const DocumentModal = ({ isOpen, onClose, onSubmit, doc, isEditing }) => {
    const [formData, setFormData] = useState({
        name: '',
        category: '',
        icon: 'FileText',
        file_url: '',
        is_active: true
    });
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (doc) {
            setFormData({
                ...doc,
                file_url: doc.file_url || doc.link || ''
            });
        } else {
            setFormData({
                name: '',
                category: 'Policies',
                icon: 'FileText',
                file_url: '',
                is_active: true
            });
        }
    }, [doc, isOpen]);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
            toast.error('Please upload a PDF file');
            return;
        }

        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            setUploading(true);
            const response = await axios.post('/api/admin/governance/upload-pdf', uploadData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success) {
                setFormData(prev => ({ ...prev, file_url: response.data.file_url }));
                toast.success('PDF uploaded successfully');
            }
        } catch (error) {
            console.error('Error uploading PDF:', error);
            toast.error('Failed to upload PDF');
        } finally {
            setUploading(false);
        }
    };

    const iconOptions = ['FileText', 'Lock', 'ShieldCheck', 'AlertCircle', 'UserCheck', 'BarChart3', 'Award', 'Eye', 'FileCheck', 'Shield', 'BookOpen', 'Users', 'TrendingUp'];
    const categoryOptions = ['Policies', 'Certificates', 'Social', 'Reports', 'Resources', 'Guidelines', 'Governance', 'Other'];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl transform transition-all">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-slate-800">
                        {isEditing ? 'Edit Document' : 'Add New Document'}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Document Name</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g. Privacy Policy"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Category</label>
                            <select
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            >
                                {categoryOptions.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Icon</label>
                            <select
                                value={formData.icon}
                                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            >
                                {iconOptions.map(icon => (
                                    <option key={icon} value={icon}>{icon}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 mt-2">
                        <input
                            type="checkbox"
                            id="is_active"
                            checked={formData.is_active !== false}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-gray-300"
                        />
                        <label htmlFor="is_active" className="text-sm font-medium text-slate-700">Set as Active</label>
                    </div>

                    <div className="pt-4 border-t border-slate-100">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Document PDF File</label>
                        <div className="flex flex-col gap-3">
                            <input
                                type="file"
                                accept=".pdf"
                                onChange={handleFileChange}
                                className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                            />
                            {uploading && <p className="text-xs text-primary-600 animate-pulse">Uploading PDF...</p>}
                            {formData.file_url && (
                                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl border border-slate-100">
                                    <FileText size={18} className="text-primary-600" />
                                    <span className="text-xs text-slate-600 truncate flex-1">{formData.file_url}</span>
                                    <button
                                        onClick={() => {
                                            let fileUrl = formData.file_url;
                                            if (fileUrl.startsWith('http')) {
                                                window.open(fileUrl, '_blank');
                                            } else {
                                                const envBackendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001/api';
                                                const backendBase = envBackendUrl.replace(/\/api$/, '');

                                                // Map legacy /pdfs/ links to proxied /api/pdfs/ links
                                                if (fileUrl.startsWith('/pdfs/')) {
                                                    fileUrl = '/api' + fileUrl;
                                                }

                                                const separator = fileUrl.startsWith('/') ? '' : '/';
                                                window.open(`${backendBase}${separator}${fileUrl}`, '_blank');
                                            }
                                        }}
                                        className="text-xs font-bold text-primary-600 hover:underline"
                                    >
                                        View
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-50 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onSubmit(formData)}
                        disabled={uploading}
                        className="px-6 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors shadow-lg shadow-primary-500/30"
                    >
                        {isEditing ? 'Save Changes' : 'Add Document'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const GovernanceTransparencyPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sectionData, setSectionData] = useState({
        section_tag: '',
        title: '',
        description: '',
        extra_text: DEFAULT_TRANSPARENCY_NOTICE,
        content_data: []
    });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);

    // Link Inserter state
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkText, setLinkText] = useState('');
    const [linkUrl, setLinkUrl] = useState('');

    const handleInsertLink = () => {
        if (!linkUrl.trim()) {
            toast.error('Please enter a valid URL (e.g. https://ahamhfc.com/investor-information)');
            return;
        }
        const label = linkText.trim() || 'investor information';
        const cleanUrl = linkUrl.trim();
        const tag = `<a href="${cleanUrl}"><strong><u>${label}</u></strong></a>`;
        const currentValue = sectionData.extra_text || '';
        const updatedValue = currentValue ? `${currentValue} ${tag}` : tag;
        setSectionData(prev => ({ ...prev, extra_text: updatedValue }));
        toast.success(`Inserted link: ${label}`);
        setShowLinkModal(false);
        setLinkText('');
        setLinkUrl('');
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const response = await axios.get('/api/admin/governance/section/transparency');
            if (response.data.success && response.data.data) {
                const data = response.data.data;
                const parsedContent = typeof data.content_data === 'string'
                    ? JSON.parse(data.content_data)
                    : (data.content_data || []);

                setSectionData({
                    section_tag: data.section_tag || '',
                    title: data.title || '',
                    description: data.description || '',
                    extra_text: data.extra_text || DEFAULT_TRANSPARENCY_NOTICE,
                    content_data: parsedContent
                });
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching transparency data:', error);
            toast.error('Failed to load data');
            setLoading(false);
        }
    };

    const handleSettingsSave = async () => {
        try {
            setSaving(true);
            await axios.put('/api/admin/governance/section/transparency', {
                ...sectionData,
                content_data: sectionData.content_data
            });
            toast.success('Section settings updated successfully');
        } catch (error) {
            console.error('Error saving transparency data:', error);
            toast.error('Failed to save data');
        } finally {
            setSaving(false);
        }
    };

    const saveContentData = async (newContentData) => {
        try {
            await axios.put('/api/admin/governance/section/transparency', {
                ...sectionData,
                content_data: newContentData
            });
            setSectionData(prev => ({ ...prev, content_data: newContentData }));
            return true;
        } catch (error) {
            console.error('Error updating documents:', error);
            toast.error('Failed to update documents');
            return false;
        }
    };

    const handleModalSubmit = async (docData) => {
        let newContentData = [...sectionData.content_data];
        if (editingIndex !== null) {
            newContentData[editingIndex] = docData;
            toast.success('Document updated');
        } else {
            newContentData.push(docData);
            toast.success('Document added');
        }

        const success = await saveContentData(newContentData);
        if (success) {
            setIsModalOpen(false);
            setEditingIndex(null);
        }
    };

    const handleDelete = async (index) => {
        if (window.confirm('Are you sure you want to delete this document?')) {
            const newContentData = sectionData.content_data.filter((_, i) => i !== index);
            await saveContentData(newContentData);
            toast.success('Document deleted');
        }
    };

    if (loading) return <div className="p-6">Loading...</div>;

    return (
        <div className="space-y-6 pb-12">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Governance - Transparency Section</h1>
                    <p className="text-slate-500 mt-1">Manage "Policies & Certificates" section content</p>
                </div>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 border-b pb-2">Section Header Settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Section Tag</label>
                        <input
                            type="text"
                            value={sectionData.section_tag}
                            onChange={(e) => setSectionData({ ...sectionData, section_tag: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g., Transparency"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Section Title</label>
                        <input
                            type="text"
                            value={sectionData.title}
                            onChange={(e) => setSectionData({ ...sectionData, title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g., Policies & Certificates"
                        />
                    </div>
                </div>
                <div className="mb-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
                    <textarea
                        value={sectionData.description}
                        onChange={(e) => setSectionData({ ...sectionData, description: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 resize-none"
                    />
                </div>
                {/* Notice / Guide Paragraph with Link Inserter */}
                <div className="border-t border-slate-100 pt-5 space-y-2 mb-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700">
                                Notice / Guide Paragraph (Between Description and Documents)
                            </label>
                            <p className="text-xs text-slate-500">
                                Appears immediately after the overview description and before the document/search area.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setShowLinkModal(!showLinkModal);
                                setLinkText('');
                                setLinkUrl('');
                            }}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-800 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition-colors border border-primary-200"
                        >
                            <LinkIcon className="w-3.5 h-3.5" />
                            <span>{showLinkModal ? 'Cancel Link' : '+ Insert Link'}</span>
                        </button>
                    </div>

                    {showLinkModal && (
                        <div className="bg-primary-50/80 border border-primary-200 rounded-xl p-3 mb-3 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-primary-900">Insert Link into Paragraph</span>
                                <span className="text-[11px] text-primary-700">e.g. https://ahamhfc.com/investor-information</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text</label>
                                    <input
                                        type="text"
                                        value={linkText}
                                        onChange={e => setLinkText(e.target.value)}
                                        placeholder="e.g. investor information"
                                        className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                                    <input
                                        type="text"
                                        value={linkUrl}
                                        onChange={e => setLinkUrl(e.target.value)}
                                        placeholder="e.g. https://ahamhfc.com/investor-information"
                                        className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-primary-500"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-1">
                                <button
                                    type="button"
                                    onClick={() => setShowLinkModal(false)}
                                    className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleInsertLink}
                                    className="px-3 py-1 text-xs font-bold bg-primary-600 text-white rounded-lg hover:bg-primary-700 shadow-sm"
                                >
                                    Insert &lt;a&gt; Tag
                                </button>
                            </div>
                        </div>
                    )}

                    <textarea
                        name="extra_text"
                        value={sectionData.extra_text || ''}
                        onChange={(e) => setSectionData({ ...sectionData, extra_text: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none text-sm"
                        placeholder='<p>Investors and stakeholders can access Aham Housing Finance’s financial and regulatory disclosures through our <a href="https://ahamhfc.com/investor-information"><strong><u>investor information</u></strong></a> section.</p>'
                    />
                    <p className="text-[11px] text-slate-500">
                        Tip: HTML anchor tags and formatting are fully supported. Use &ldquo;+ Insert Link&rdquo; to quickly format links.
                    </p>

                    {/* Live Preview */}
                    {sectionData.extra_text && (
                        <div className="mt-3 bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2">
                            <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                    <span className="text-xs font-bold uppercase tracking-wider text-slate-700">PARAGRAPH LIVE PREVIEW</span>
                                </div>
                                <span className="text-[11px] text-slate-500">Live preview with clickable styled link</span>
                            </div>
                            <div
                                className="text-slate-700 text-sm leading-relaxed [&_a]:text-primary-600 [&_a]:underline hover:[&_a]:text-primary-700 [&_a]:font-semibold [&_a]:transition-colors"
                                dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(sectionData.extra_text) }}
                            />
                        </div>
                    )}
                </div>

                <div className="flex justify-end">
                    <button
                        onClick={handleSettingsSave}
                        disabled={saving}
                        className="flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-xl hover:bg-primary-700 disabled:opacity-50 shadow-md transition-all font-medium"
                    >
                        <Save size={18} />
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {/* Documents Grid */}
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">Documents</h2>
                        <p className="text-sm text-slate-500">Manage list of downloadable policies and documents</p>
                    </div>
                    <button
                        onClick={() => {
                            setEditingIndex(null);
                            setIsModalOpen(true);
                        }}
                        className="flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-xl hover:bg-primary-700 shadow-lg hover:shadow-xl transition-all font-semibold"
                    >
                        <Plus size={20} /> Add Document
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sectionData.content_data.map((doc, index) => {
                        const IconComponent = LucideIconMap[doc.icon] || FileText;

                        return (
                            <div
                                key={index}
                                className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-all group"
                            >
                                {/* Blue Top Bar */}
                                <div className="h-2 bg-gradient-to-r from-blue-500 to-indigo-600"></div>

                                <div className="p-5">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="p-3 rounded-xl bg-blue-50 text-blue-600 shadow-sm">
                                            <IconComponent size={24} />
                                        </div>
                                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => {
                                                    setEditingIndex(index);
                                                    setIsModalOpen(true);
                                                }}
                                                className="p-2 hover:bg-slate-50 rounded-lg text-slate-400 hover:text-primary-600 transition-colors"
                                                title="Edit"
                                            >
                                                <HeroIconsOutline.PencilIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(index)}
                                                className="p-2 hover:bg-slate-50 rounded-lg text-slate-400 hover:text-red-600 transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 size={20} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Status & Category Badge */}
                                    <div className="flex items-center justify-between mb-4">
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${doc.is_active !== false ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                                            {doc.is_active !== false ? 'Active' : 'Inactive'}
                                        </span>
                                        <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full uppercase tracking-wider">
                                            {doc.category || 'Other'}
                                        </span>
                                    </div>

                                    <div className="space-y-1">
                                        <h3 className="font-bold text-slate-800 text-base">{doc.name}</h3>
                                    </div>
                                </div>
                            </div>
                        )
                    })}

                    {sectionData.content_data.length === 0 && (
                        <div className="col-span-full py-12 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                                <FileText className="h-8 w-8 text-slate-300" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-800 mb-1">No documents added</h3>
                            <p className="text-slate-500 mb-6">Start adding your policies and certificates.</p>
                            <button
                                onClick={() => {
                                    setEditingIndex(null);
                                    setIsModalOpen(true);
                                }}
                                className="text-primary-600 font-medium hover:text-primary-700"
                            >
                                + Add First Document
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <DocumentModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleModalSubmit}
                doc={editingIndex !== null ? sectionData.content_data[editingIndex] : null}
                isEditing={editingIndex !== null}
            />
        </div>
    );
};

export default GovernanceTransparencyPage;
